import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { CaretLeft, CaretRight, CalendarBlank, Lightning, ListChecks, FileText, UsersThree, MagnifyingGlass, Briefcase, ArrowsOutCardinal } from "@phosphor-icons/react";
import { toast } from "sonner";
import { todayLocalISO } from "../lib/dateUtil";

const KIND_STYLE = {
  job:           { icon: Briefcase,  bg: "bg-blue-100 dark:bg-blue-950/40",     border: "border-blue-300 dark:border-blue-800",     text: "text-blue-900 dark:text-blue-200",     label: "Job" },
  emergency_job: { icon: Lightning,  bg: "bg-rose-100 dark:bg-rose-950/40",     border: "border-rose-400 dark:border-rose-800",     text: "text-rose-900 dark:text-rose-200",     label: "Emergency" },
  task:          { icon: ListChecks, bg: "bg-emerald-100 dark:bg-emerald-950/40", border: "border-emerald-300 dark:border-emerald-800", text: "text-emerald-900 dark:text-emerald-200", label: "Task" },
  work_order:    { icon: FileText,   bg: "bg-violet-100 dark:bg-violet-950/40", border: "border-violet-300 dark:border-violet-800", text: "text-violet-900 dark:text-violet-200", label: "WO" },
};

function getMondayISO(date = new Date()) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  if (day !== 1) d.setUTCDate(d.getUTCDate() - (day - 1));
  return d.toISOString().slice(0, 10);
}

function shiftWeek(isoDate, deltaDays) {
  const d = new Date(isoDate + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

function formatWeekLabel(startISO, endISO) {
  const s = new Date(startISO + "T00:00:00Z");
  const e = new Date(endISO + "T00:00:00Z");
  const fmt = (d) => d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  return `${fmt(s)} – ${fmt(e)}`;
}

function DayHeader({ iso }) {
  const d = new Date(iso + "T00:00:00Z");
  const todayISO = todayLocalISO();
  const isToday = iso === todayISO;
  const name = d.toLocaleDateString(undefined, { weekday: "short" });
  const num = d.toLocaleDateString(undefined, { day: "numeric" });
  return (
    <div className={`text-center py-2 px-1 border-b border-border ${isToday ? "bg-primary/10" : ""}`}>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{name}</p>
      <p className={`text-sm font-bold ${isToday ? "text-primary" : "text-slate-900 dark:text-slate-100"}`}>{num}</p>
    </div>
  );
}

function EventChip({ e, onDragStart, onLongPress, isDragging }) {
  const s = KIND_STYLE[e.kind] || KIND_STYLE.job;
  const Icon = s.icon;

  // Long-press detection for mobile (600ms)
  const pressTimer = useRef(null);
  const pressStartXY = useRef({ x: 0, y: 0 });
  const triggered = useRef(false);

  const handleTouchStart = (evt) => {
    triggered.current = false;
    pressStartXY.current = { x: evt.touches[0].clientX, y: evt.touches[0].clientY };
    pressTimer.current = setTimeout(() => {
      triggered.current = true;
      if (window.navigator?.vibrate) window.navigator.vibrate(20);
      onLongPress?.(e);
    }, 600);
  };
  const handleTouchMove = (evt) => {
    const dx = Math.abs(evt.touches[0].clientX - pressStartXY.current.x);
    const dy = Math.abs(evt.touches[0].clientY - pressStartXY.current.y);
    if (dx > 8 || dy > 8) { clearTimeout(pressTimer.current); }
  };
  const handleTouchEnd = (evt) => {
    clearTimeout(pressTimer.current);
    if (triggered.current) { evt.preventDefault(); }
  };

  return (
    <Link to={e.link}
          draggable
          onDragStart={(evt) => onDragStart?.(evt, e)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className={`block rounded-md border ${s.bg} ${s.border} ${s.text} px-2 py-1.5 text-[11px] leading-tight hover:shadow-sm transition-all cursor-grab active:cursor-grabbing select-none ${isDragging ? "opacity-40 scale-95" : ""}`}
          data-testid={`schedule-event-${e.kind}-${e.id}`}
          title={`${e.title} — hold to move on mobile, or drag with mouse to reassign`}>
      <div className="flex items-center gap-1 font-semibold">
        <Icon className="h-3 w-3 shrink-0" weight="duotone" />
        <span className="truncate">{e.reference || s.label}</span>
      </div>
      <p className="truncate opacity-90 mt-0.5">{e.title}</p>
      {e.property && <p className="truncate text-[10px] opacity-60 mt-0.5">{e.property}</p>}
    </Link>
  );
}

function CapacityBar({ ratio, band, count, cap }) {
  const bg = band === "red" ? "bg-rose-500" : band === "amber" ? "bg-amber-500" : "bg-emerald-500";
  const label = band === "red" ? "Full" : band === "amber" ? "Nearly full" : "Available";
  const width = Math.min(100, Math.round((ratio || 0) * 100));
  return (
    <div className="flex items-center gap-1.5" title={`${label} — ${count}/${cap} events this week`}
         data-testid={`schedule-capacity-${band}`}>
      <div className="h-1 w-12 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
        <div className={`h-full ${bg} transition-all`} style={{ width: `${width}%` }} />
      </div>
      <span className="text-[9px] font-semibold text-slate-500 dark:text-slate-400 tabular-nums">{count}/{cap}</span>
    </div>
  );
}

function PersonRow({ person, days, events, onDragStart, onDropOnPerson, onLongPress, draggedId, dragOverPerson }) {
  const byDay = useMemo(() => {
    const map = {};
    days.forEach((d) => { map[d] = []; });
    (events || []).forEach((e) => { if (map[e.date]) map[e.date].push(e); });
    return map;
  }, [days, events]);

  const canReceive = !!draggedId;
  const isHighlighted = canReceive && dragOverPerson === person.id;

  const handleDragOver = (evt) => {
    if (!canReceive) return;
    evt.preventDefault();
    evt.dataTransfer.dropEffect = "move";
  };
  const handleDrop = (evt) => {
    evt.preventDefault();
    onDropOnPerson?.(person);
  };

  return (
    <div className={`grid grid-cols-[180px_repeat(7,1fr)] border-b border-border transition-colors ${isHighlighted ? "bg-primary/10" : ""}`}
         onDragOver={handleDragOver}
         onDrop={handleDrop}
         data-testid={`schedule-row-${person.id}`}>
      <div className="px-3 py-2 bg-muted/30 border-r border-border flex items-center gap-2 min-w-0">
        <div className={`h-8 w-8 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold
          ${person.kind === "in_house" ? "bg-amber-500/15 text-amber-700 dark:text-amber-300" : "bg-primary/15 text-primary"}`}>
          {person.initials}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold truncate">{person.name}</p>
          <p className="text-[10px] text-muted-foreground truncate capitalize">{person.trade || (person.kind === "in_house" ? "in-house" : "supplier")}</p>
          {person.capacity_cap != null && (
            <CapacityBar ratio={person.capacity_ratio} band={person.capacity_band}
                         count={person.event_count} cap={person.capacity_cap} />
          )}
        </div>
      </div>
      {days.map((d) => (
        <div key={d} className="border-r border-border last:border-r-0 p-1 space-y-1 min-h-[72px]"
             data-testid={`schedule-cell-${person.id}-${d}`}>
          {byDay[d].map((e) => (
            <EventChip key={`${e.kind}-${e.id}`} e={e}
                       onDragStart={onDragStart}
                       onLongPress={onLongPress}
                       isDragging={draggedId === `${e.kind}:${e.id}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

function MovePickerDialog({ open, event, people, onCancel, onPick }) {
  const [filter, setFilter] = useState("");
  useEffect(() => { if (!open) setFilter(""); }, [open]);
  if (!event) return null;
  const filtered = people
    .filter((p) => p.name.toLowerCase().includes(filter.toLowerCase()) || (p.trade || "").toLowerCase().includes(filter.toLowerCase()))
    .slice(0, 50);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel?.(); }}>
      <DialogContent data-testid="schedule-move-dialog">
        <DialogHeader>
          <DialogTitle>Reassign this {event.kind.replace("_", " ")}</DialogTitle>
          <DialogDescription className="truncate">{event.reference ? `${event.reference} · ` : ""}{event.title}</DialogDescription>
        </DialogHeader>
        <Input autoFocus placeholder="Search operative or supplier…" value={filter} onChange={(e) => setFilter(e.target.value)}
               data-testid="schedule-move-search" />
        <div className="max-h-80 overflow-y-auto divide-y divide-border border border-border rounded-md">
          {filtered.length === 0 && <p className="p-4 text-xs text-muted-foreground">No matches.</p>}
          {filtered.map((p) => (
            <button key={p.id} onClick={() => onPick?.(p)}
                    className="w-full text-left px-3 py-2.5 hover:bg-muted text-xs flex items-center gap-2"
                    data-testid={`schedule-move-pick-${p.id}`}>
              <div className={`h-8 w-8 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold
                ${p.kind === "in_house" ? "bg-amber-500/15 text-amber-700" : "bg-primary/15 text-primary"}`}>
                {p.initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{p.name}</p>
                <p className="text-[10px] text-muted-foreground truncate capitalize">{p.trade || p.kind.replace("_", " ")}</p>
              </div>
              {p.capacity_cap != null && (
                <CapacityBar ratio={p.capacity_ratio} band={p.capacity_band}
                             count={p.event_count} cap={p.capacity_cap} />
              )}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SchedulePage() {
  const [weekStart, setWeekStart] = useState(() => getMondayISO());
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trade, setTrade] = useState("all");
  const [query, setQuery] = useState("");
  const [kindFilter, setKindFilter] = useState("all");

  // DnD state
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [dragOverPerson, setDragOverPerson] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null); // for long-press dialog

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/schedule/grid?week_start=${weekStart}`);
      setGrid(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load schedule");
    } finally { setLoading(false); }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  const doReassign = async (event, person) => {
    if (!event || !person) return;
    if (event.person_id === person.id) return; // no-op
    // Work orders can only go to suppliers
    if (event.kind === "work_order" && person.kind !== "vendor") {
      toast.error("Works orders can only be reassigned to suppliers.");
      return;
    }
    try {
      await API.post("/schedule/reassign", {
        kind: event.kind,
        entity_id: event.id,
        target_person_id: person.id,
        target_kind: person.kind,
      });
      toast.success(`Moved to ${person.name}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not reassign");
    }
  };

  const handleDragStart = (evt, event) => {
    setDraggedEvent(event);
    try {
      evt.dataTransfer.effectAllowed = "move";
      evt.dataTransfer.setData("text/plain", `${event.kind}:${event.id}`);
    } catch {}
  };
  useEffect(() => {
    const onEnd = () => { setDraggedEvent(null); setDragOverPerson(null); };
    window.addEventListener("dragend", onEnd);
    window.addEventListener("drop", onEnd);
    return () => {
      window.removeEventListener("dragend", onEnd);
      window.removeEventListener("drop", onEnd);
    };
  }, []);

  const handleDropOnPerson = (person) => {
    if (draggedEvent) { doReassign(draggedEvent, person); }
    setDraggedEvent(null); setDragOverPerson(null);
  };

  const filtered = useMemo(() => {
    if (!grid) return { people: [], events: [] };
    const q = query.trim().toLowerCase();
    let events = grid.events;
    if (kindFilter !== "all") events = events.filter((e) => e.kind === kindFilter);
    const allowedPersonIds = new Set(events.map((e) => e.person_id));
    let people = grid.people;
    if (trade !== "all") {
      people = people.filter((p) =>
        (p.trades || []).map((t) => (t || "").toLowerCase()).includes(trade.toLowerCase())
        || (p.trade || "").toLowerCase() === trade.toLowerCase()
      );
    }
    if (q) {
      people = people.filter((p) => p.name.toLowerCase().includes(q) || (p.trade || "").toLowerCase().includes(q));
    }
    const peopleOut = people.filter((p) => allowedPersonIds.has(p.id) || (!q && trade === "all"));
    return { people: peopleOut, events };
  }, [grid, trade, query, kindFilter]);

  // Listener for per-row dragenter so we can highlight the target (cheap via window-level tracking)
  const onRowDragEnter = useCallback((personId) => setDragOverPerson(personId), []);

  if (loading && !grid) {
    return <div className="flex items-center justify-center py-16 text-muted-foreground">Loading schedule…</div>;
  }

  return (
    <div className="space-y-4" data-testid="schedule-page">
      <div className="flex items-center gap-3 flex-wrap">
        <CalendarBlank className="h-7 w-7 text-primary" weight="duotone" />
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Team Schedule</h1>
          <p className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <ArrowsOutCardinal className="h-3 w-3" /> Drag any chip to another person's row to reassign · hold to move on mobile
          </p>
        </div>

        <div className="flex items-center gap-1.5 bg-card border border-border rounded-md p-0.5">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setWeekStart(shiftWeek(weekStart, -7))}
                  data-testid="schedule-prev-week"><CaretLeft className="h-4 w-4" /></Button>
          <button onClick={() => setWeekStart(getMondayISO())}
                  className="text-xs font-semibold px-2 hover:text-primary" data-testid="schedule-this-week">Today</button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setWeekStart(shiftWeek(weekStart, 7))}
                  data-testid="schedule-next-week"><CaretRight className="h-4 w-4" /></Button>
        </div>
        <div className="text-xs font-semibold text-slate-700 dark:text-slate-300 bg-muted px-3 py-1.5 rounded-md" data-testid="schedule-week-label">
          {grid && formatWeekLabel(grid.week_start, grid.week_end)}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-[320px]">
          <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input className="pl-8 h-9 text-sm" placeholder="Search by name or trade…" value={query}
                 onChange={(e) => setQuery(e.target.value)} data-testid="schedule-search" />
        </div>
        <Select value={trade} onValueChange={setTrade}>
          <SelectTrigger className="h-9 w-[180px]" data-testid="schedule-trade-filter">
            <SelectValue placeholder="All trades" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All trades</SelectItem>
            {(grid?.trades || []).map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={kindFilter} onValueChange={setKindFilter}>
          <SelectTrigger className="h-9 w-[180px]" data-testid="schedule-kind-filter">
            <SelectValue placeholder="All work types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All work types</SelectItem>
            <SelectItem value="job">Jobs only</SelectItem>
            <SelectItem value="emergency_job">Emergencies only</SelectItem>
            <SelectItem value="task">Tasks only</SelectItem>
            <SelectItem value="work_order">Works orders only</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <UsersThree className="h-3.5 w-3.5" />
          <span data-testid="schedule-counts">{filtered.people.length} people · {filtered.events.length} events</span>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap text-[11px]">
        {Object.entries(KIND_STYLE).map(([k, s]) => {
          const Icon = s.icon;
          const count = grid?.counts?.by_kind?.[k] || 0;
          return (
            <span key={k} className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 border ${s.bg} ${s.border} ${s.text}`}>
              <Icon className="h-3 w-3" weight="duotone" /> {s.label} <span className="opacity-60">· {count}</span>
            </span>
          );
        })}
      </div>

      {filtered.people.length === 0 ? (
        <div className="border-2 border-dashed border-border rounded-lg py-16 text-center" data-testid="schedule-empty">
          <CalendarBlank className="h-12 w-12 mx-auto text-muted-foreground/50" weight="duotone" />
          <p className="mt-3 font-semibold text-slate-700 dark:text-slate-200">No assignments this week</p>
          <p className="text-xs text-muted-foreground mt-1">Scheduled work for this week will appear here as people are assigned.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden overflow-x-auto">
          <div className="min-w-[900px]"
               onDragEnter={(e) => {
                 // Track which row we're over to highlight — fallback tracker
                 const row = e.target.closest?.("[data-testid^='schedule-row-']");
                 if (row) {
                   const pid = row.getAttribute("data-testid").replace("schedule-row-", "");
                   onRowDragEnter(pid);
                 }
               }}>
            <div className="grid grid-cols-[180px_repeat(7,1fr)] bg-muted/40 sticky top-0 z-10">
              <div className="px-3 py-2 border-r border-border flex items-center">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Person</span>
              </div>
              {(grid?.days || []).map((d) => <DayHeader key={d} iso={d} />)}
            </div>
            {filtered.people.map((p) => {
              const personEvents = filtered.events.filter((e) => e.person_id === p.id);
              return (
                <PersonRow key={p.id} person={p} days={grid.days} events={personEvents}
                           onDragStart={handleDragStart}
                           onDropOnPerson={handleDropOnPerson}
                           onLongPress={(evt) => setMoveTarget(evt)}
                           draggedId={draggedEvent ? `${draggedEvent.kind}:${draggedEvent.id}` : null}
                           dragOverPerson={dragOverPerson} />
              );
            })}
          </div>
        </div>
      )}

      <MovePickerDialog open={!!moveTarget} event={moveTarget} people={grid?.people || []}
                        onCancel={() => setMoveTarget(null)}
                        onPick={(p) => { doReassign(moveTarget, p); setMoveTarget(null); }} />
    </div>
  );
}
